import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppDispatch, RootState } from '../../store';
import { fetchCover, saveCover } from '../../store/coverSlice';
import { Cover as CoverData } from '../../api';
import { TopNav } from '../../components/TopNav/TopNav';
import {
  BookCover,
  coverCatalog,
  coverByValue,
  defaultCover,
  CoverEntry,
  encodeCover,
  decodeCover,
  isCustomCover,
  customUrlFromId,
  idFromCustomUrl,
} from '../../components/BookCover';
import {
  PLATE_PRESETS,
  asPlatePreset,
  type PlatePreset,
} from '../../components/BookCover/BookCover';
import '../../styles/margin.css';
import './Cover.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const Cover: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('cover');
  const { t: tCommon } = useTranslation('common');

  const coverData = useSelector<RootState, CoverData | null>(
    (state) => state.cover.value
  );

  const [authorName, setAuthorName] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [selected, setSelected] = useState<CoverEntry>(defaultCover);
  const [customUrl, setCustomUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [platePreset, setPlatePreset] = useState<PlatePreset>('frame');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isCustom = !!customUrl;
  const activeBackground = isCustom ? customUrl : selected.url;

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
      return;
    }
    if (!templateId) navigate('/forms');
  }, [navigate, templateId]);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchCover(Number(templateId)));
    }
  }, [dispatch, templateId]);

  useEffect(() => {
    if (coverData) {
      setAuthorName(coverData.authorName || '');
      setBookTitle(coverData.bookTitle || '');
      setPartnerName(coverData.receiverName || '');
      const paintingId = decodeCover(coverData.coverUrl);
      if (isCustomCover(paintingId)) {
        setCustomUrl(customUrlFromId(paintingId));
      } else {
        const entry = coverByValue(paintingId);
        setSelected(entry ?? defaultCover);
        setCustomUrl('');
      }
      // platePreset из БД может быть null (старые записи до миграции) —
      // в этом случае оставляем стартовый 'frame'.
      const stored = asPlatePreset(coverData.platePreset);
      if (stored) setPlatePreset(stored);
      setHasChanges(false);
    }
  }, [coverData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/forms');
  };

  const handleSave = async () => {
    if (!templateId || isSaving) return;
    setIsSaving(true);
    try {
      const effectiveId = isCustom ? idFromCustomUrl(customUrl) : selected.id;
      await dispatch(
        saveCover({
          templateId: Number(templateId),
          bookTitle,
          coverUrl: encodeCover(effectiveId),
          authorName,
          receiverName: partnerName,
          platePreset,
        })
      );
      setHasChanges(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2400);
    } catch (error) {
      console.error('Error saving cover:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelect = (entry: CoverEntry) => {
    if (entry.id !== selected.id || isCustom) {
      setSelected(entry);
      setCustomUrl('');
      setHasChanges(true);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert(t('alert_bad_format'));
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setCustomUrl(response.data.url);
      setHasChanges(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('alert_upload_failed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveCustom = () => {
    setCustomUrl('');
    setHasChanges(true);
  };

  const receiverLabel = (id: string | undefined) => {
    if (id === '65fb40c5b63f0df17f6ce6ae') return t('receiver_mom') as string;
    if (id === '65fb7789f6d6c9118d3caead') return t('receiver_sister') as string;
    if (id === '661970fd80f5c5317e0882c3') return t('receiver_friend') as string;
    return t('receiver_partner') as string;
  };

  const validateBookTitle = (title: string) => {
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
    return title.length <= 30 && !emojiRegex.test(title);
  };

  const handleBookTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    if (validateBookTitle(title)) {
      setBookTitle(title);
      setHasChanges(true);
    }
  };

  const handleInputChange = (setter: (val: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  return (
    <div className="mg-page mg-cover">
      <TopNav
        onLogout={handleLogout}
        back={handleBack}
        title={t('rite_now') as string}
      />

      <main className="mg-cover__main mg-split">

        <section className="mg-cover__stage mg-split__preview">
          <BookCover
            className="mg-cover__preview"
            background={activeBackground}
            title={bookTitle}
            author={authorName}
            platePreset={platePreset}
            printPlate
          />
        </section>

        <div className="mg-split__ui">

        <section className="mg-cover__inputs">
          

          <div className="mg-cover__fields">
            <div className="mg-field">
              <label className="mg-field__label">{t('field_book_title')}</label>
              <input
                type="text"
                className="mg-input"
                placeholder={t('placeholder_book_title') as string}
                value={bookTitle}
                onChange={handleBookTitleChange}
                maxLength={30}
              />
              <div className="mg-cover__count mg-meta">
                {bookTitle.length} / 30
              </div>
            </div>

            <div className="mg-field">
              <label className="mg-field__label">{t('field_your_name')}</label>
              <input
                type="text"
                className="mg-input"
                placeholder={t('placeholder_author') as string}
                value={authorName}
                onChange={handleInputChange(setAuthorName)}
              />
            </div>
          </div>

          <div className="mg-cover__plate-presets">
            <div className="mg-label">{t('plate_preset_head')}</div>
            <div className="mg-cover__plate-presets-row">
              {PLATE_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`mg-cover__plate-preset ${platePreset === p ? 'mg-cover__plate-preset--active' : ''}`}
                  onClick={() => {
                    setPlatePreset(p);
                    setHasChanges(true);
                  }}
                >
                  {t(`plate_preset_${p}`)}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mg-cover__gallery">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <div className="mg-cover__thumbs">
            <button
              type="button"
              className={`mg-cover__thumb mg-cover__thumb--upload ${isCustom ? 'mg-cover__thumb--active' : ''}`}
              onClick={handleUploadClick}
              disabled={isUploading}
              title={t('custom_photo_head') as string}
            >
              {isCustom ? (
                <>
                  <img
                    src={customUrl}
                    alt=""
                    className="mg-cover__thumb-img"
                    loading="lazy"
                  />
                  <span
                    className="mg-cover__thumb-remove"
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustom();
                    }}
                  >
                    {t('custom_photo_remove')}
                  </span>
                </>
              ) : (
                <span className="mg-cover__thumb-upload-label">
                  {isUploading
                    ? t('upload_uploading')
                    : t('upload_new')}
                </span>
              )}
            </button>
            {coverCatalog.map((entry) => {
              const isActive = !isCustom && entry.id === selected.id;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`mg-cover__thumb ${isActive ? 'mg-cover__thumb--active' : ''}`}
                  onClick={() => handleSelect(entry)}
                >
                  <img
                    src={entry.url}
                    alt=""
                    className="mg-cover__thumb-img"
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        </section>

        <section className="mg-cover__cta">
          <button
            className="mg-btn"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            type="button"
          >
            {isSaving
              ? t('cta_saving')
              : hasChanges
                ? t('cta_approve')
                : t('cta_approved')}
          </button>
        </section>
        </div>
      </main>
    </div>
  );
};
